import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Select,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  DatePicker,
  InputNumber,
  message,
  Alert
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface Station {
  _id: string;
  name: string;
  address: string;
}

interface PriceComparison {
  stationId: string;
  stationName: string;
  address: string;
  price: number;
  isMyStation: boolean;
  rank: number;
}

interface MarketAnalysis {
  overallStats: {
    average: number;
    minimum: number;
    maximum: number;
    median: number;
    totalStations: number;
    totalPriceRecords: number;
  };
  dailyTrends: Array<{
    date: string;
    averagePrice: number;
    stationCount: number;
  }>;
  cityAnalysis: Array<{
    city: string;
    averagePrice: number;
    minPrice: number;
    maxPrice: number;
    stationCount: number;
  }>;
}

interface CompetitionReport {
  station: {
    id: string;
    name: string;
    address: string;
  };
  myPrice: number | null;
  marketAverage: number;
  competitivePosition: 'below' | 'above' | 'equal' | 'unknown';
  priceDifference: number | null;
  priceDifferencePercent: number | null;
  rank: number;
  totalCompetitors: number;
  competitors: PriceComparison[];
  statistics: {
    minimum: number;
    maximum: number;
    median: number;
  };
  recommendations: Array<{
    type: string;
    message: string;
    priority: 'high' | 'medium' | 'low';
  }>;
}

const CompetitionAnalysis: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedStation, setSelectedStation] = useState<string>('');
  const [fuelType, setFuelType] = useState<string>('benzin');
  const [radius, setRadius] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('comparison');

  // Regional Comparison State
  const [priceComparison, setPriceComparison] = useState<any>(null);

  // Market Analysis State
  const [marketAnalysis, setMarketAnalysis] = useState<MarketAnalysis | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Competition Report State
  const [competitionReport, setCompetitionReport] = useState<CompetitionReport | null>(null);

  useEffect(() => {
    fetchStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
      if (response.length > 0 && !selectedStation) {
        setSelectedStation(response[0]._id);
      }
    } catch (error: any) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchRegionalComparison = async () => {
    if (!selectedStation) {
      message.warning('Lütfen bir istasyon seçin');
      return;
    }

    setLoading(true);
    try {
      const data = await stationOwnerAPI.getRegionalPriceComparison({
        stationId: selectedStation,
        radius,
        fuelType
      });
      setPriceComparison(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fiyat karşılaştırması yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketAnalysis = async () => {
    setLoading(true);
    try {
      const params: any = {
        fuelType
      };
      
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const data = await stationOwnerAPI.getMarketAnalysis(params);
      setMarketAnalysis(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Pazar analizi yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitionReport = async () => {
    if (!selectedStation) {
      message.warning('Lütfen bir istasyon seçin');
      return;
    }

    setLoading(true);
    try {
      const data = await stationOwnerAPI.getCompetitionReport({
        stationId: selectedStation,
        fuelType,
        radius
      });
      setCompetitionReport(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Rekabet raporu oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const comparisonColumns = [
    {
      title: 'Sıra',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (rank: number, record: PriceComparison) => (
        <Space>
          {record.isMyStation && <Tag color="blue">Benim</Tag>}
          <Text strong>{rank}</Text>
        </Space>
      )
    },
    {
      title: 'İstasyon Adı',
      dataIndex: 'stationName',
      key: 'stationName',
      render: (name: string, record: PriceComparison) => (
        <Space>
          {record.isMyStation && <CheckCircleOutlined style={{ color: '#1890ff' }} />}
          <Text strong={record.isMyStation}>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address'
    },
    {
      title: 'Fiyat (₺/L)',
      dataIndex: 'price',
      key: 'price',
      sorter: (a: PriceComparison, b: PriceComparison) => a.price - b.price,
      render: (price: number, record: PriceComparison) => (
        <Text strong={record.isMyStation} style={{ color: record.isMyStation ? '#1890ff' : undefined }}>
          {price.toFixed(2)}
        </Text>
      )
    }
  ];

  const tabItems = [
    {
      key: 'comparison',
      label: 'Bölgesel Fiyat Karşılaştırması',
      icon: <BarChartOutlined />,
      children: (
        <div>
          <Card>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="İstasyon Seçin"
                  value={selectedStation}
                  onChange={setSelectedStation}
                  style={{ width: '100%' }}
                >
                  {stations.map(station => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={fuelType}
                  onChange={setFuelType}
                  style={{ width: '100%' }}
                >
                  <Option value="benzin">Benzin</Option>
                  <Option value="diesel">Dizel</Option>
                  <Option value="lpg">LPG</Option>
                  <Option value="premium_benzin">Premium Benzin</Option>
                  <Option value="premium_diesel">Premium Dizel</Option>
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <InputNumber
                  placeholder="Yarıçap (km)"
                  value={radius}
                  onChange={(value) => setRadius(value || 10)}
                  min={1}
                  max={50}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Button
                  type="primary"
                  onClick={fetchRegionalComparison}
                  loading={loading}
                  block
                >
                  Analiz Et
                </Button>
              </Col>
            </Row>

            {priceComparison && (
              <>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Benim Sıram"
                        value={priceComparison.myStation.rank}
                        suffix={`/ ${priceComparison.statistics.totalStations}`}
                        prefix={<TrophyOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Benim Fiyatım"
                        value={priceComparison.myStation.price}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Ortalama Fiyat"
                        value={priceComparison.statistics.average}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="En Düşük Fiyat"
                        value={priceComparison.statistics.minimum}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                </Row>

                <Card title="Fiyat Karşılaştırması">
                  <Table
                    dataSource={priceComparison.comparison}
                    columns={comparisonColumns}
                    rowKey="stationId"
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              </>
            )}
          </Card>
        </div>
      )
    },
    {
      key: 'market',
      label: 'Pazar Analizi',
      icon: <LineChartOutlined />,
      children: (
        <div>
          <Card>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={6}>
                <Select
                  value={fuelType}
                  onChange={setFuelType}
                  style={{ width: '100%' }}
                >
                  <Option value="benzin">Benzin</Option>
                  <Option value="diesel">Dizel</Option>
                  <Option value="lpg">LPG</Option>
                  <Option value="premium_benzin">Premium Benzin</Option>
                  <Option value="premium_diesel">Premium Dizel</Option>
                </Select>
              </Col>
              <Col xs={24} sm={10}>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Button
                  type="primary"
                  onClick={fetchMarketAnalysis}
                  loading={loading}
                  block
                >
                  Analiz Et
                </Button>
              </Col>
            </Row>

            {marketAnalysis && (
              <>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Ortalama Fiyat"
                        value={marketAnalysis.overallStats.average}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="En Düşük"
                        value={marketAnalysis.overallStats.minimum}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="En Yüksek"
                        value={marketAnalysis.overallStats.maximum}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Toplam İstasyon"
                        value={marketAnalysis.overallStats.totalStations}
                      />
                    </Card>
                  </Col>
                </Row>

                {marketAnalysis.dailyTrends.length > 0 && (
                  <Card title="Günlük Fiyat Trendi" style={{ marginBottom: 24 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={marketAnalysis.dailyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="averagePrice"
                          stroke="#1890ff"
                          name="Ortalama Fiyat (₺/L)"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}

                {marketAnalysis.cityAnalysis.length > 0 && (
                  <Card title="Şehir Bazında Analiz">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={marketAnalysis.cityAnalysis.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="city" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="averagePrice" name="Ortalama Fiyat (₺/L)" fill="#1890ff" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </>
            )}
          </Card>
        </div>
      )
    },
    {
      key: 'report',
      label: 'Rekabet Raporu',
      icon: <TrophyOutlined />,
      children: (
        <div>
          <Card>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="İstasyon Seçin"
                  value={selectedStation}
                  onChange={setSelectedStation}
                  style={{ width: '100%' }}
                >
                  {stations.map(station => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={fuelType}
                  onChange={setFuelType}
                  style={{ width: '100%' }}
                >
                  <Option value="benzin">Benzin</Option>
                  <Option value="diesel">Dizel</Option>
                  <Option value="lpg">LPG</Option>
                  <Option value="premium_benzin">Premium Benzin</Option>
                  <Option value="premium_diesel">Premium Dizel</Option>
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <InputNumber
                  placeholder="Yarıçap (km)"
                  value={radius}
                  onChange={(value) => setRadius(value || 10)}
                  min={1}
                  max={50}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Button
                  type="primary"
                  onClick={fetchCompetitionReport}
                  loading={loading}
                  block
                >
                  Rapor Oluştur
                </Button>
              </Col>
            </Row>

            {competitionReport && (
              <>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Rekabetçi Konum"
                        value={
                          competitionReport.competitivePosition === 'below' ? 'Avantajlı' :
                          competitionReport.competitivePosition === 'above' ? 'Pahalı' :
                          competitionReport.competitivePosition === 'equal' ? 'Eşit' : 'Bilinmiyor'
                        }
                        prefix={
                          competitionReport.competitivePosition === 'below' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                          competitionReport.competitivePosition === 'above' ? <WarningOutlined style={{ color: '#ff4d4f' }} /> :
                          <InfoCircleOutlined />
                        }
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Sıralama"
                        value={competitionReport.rank}
                        suffix={`/ ${competitionReport.totalCompetitors}`}
                        prefix={<TrophyOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Fiyat Farkı"
                        value={competitionReport.priceDifferencePercent || 0}
                        precision={2}
                        suffix="%"
                        valueStyle={{
                          color: competitionReport.priceDifferencePercent && competitionReport.priceDifferencePercent > 0 ? '#ff4d4f' : '#52c41a'
                        }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card>
                      <Statistic
                        title="Pazar Ortalaması"
                        value={competitionReport.marketAverage}
                        precision={2}
                        suffix="₺/L"
                      />
                    </Card>
                  </Col>
                </Row>

                {competitionReport.recommendations.length > 0 && (
                  <Card title="Öneriler" style={{ marginBottom: 24 }}>
                    {competitionReport.recommendations.map((rec, index) => (
                      <Alert
                        key={index}
                        message={rec.message}
                        type={
                          rec.priority === 'high' ? 'error' :
                          rec.priority === 'medium' ? 'warning' : 'info'
                        }
                        style={{ marginBottom: 12 }}
                        showIcon
                      />
                    ))}
                  </Card>
                )}

                <Card title="Rakip İstasyonlar">
                  <Table
                    dataSource={competitionReport.competitors}
                    columns={comparisonColumns}
                    rowKey="stationId"
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              </>
            )}
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '24px' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Rekabet Analizi
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default CompetitionAnalysis;

